import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Plus,
    Loader2,
    Anchor,
    Calendar,
    MapPin,
    User,
    ChevronRight,
    ClipboardList,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { TID } from "@/lib/testIds";
import { useRealtimeChannel } from "@/context/RealtimeContext";
import FocusHeader from "@/components/FocusHeader";
import PreLaunchChecklistModal from "@/components/PreLaunchChecklistModal";

const OUTCOME_META = {
    planned: { bg: "#333333", fg: "#A1A1AA", label: "PLANNED" },
    "in-progress": { bg: "#FFEA00", fg: "#050505", label: "IN PROGRESS" },
    success: { bg: "#00E676", fg: "#050505", label: "SUCCESS" },
    partial: { bg: "#FF5500", fg: "#050505", label: "PARTIAL" },
    failed: { bg: "#FF1744", fg: "#FFFFFF", label: "FAILED" },
    aborted: { bg: "#666", fg: "#FFFFFF", label: "ABORTED" },
};

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function Trials() {
    const nav = useNavigate();
    const [trials, setTrials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [checklistOpen, setChecklistOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/trials");
            setTrials(data);
        } catch (e) {
            console.error("[Trials] load failed:", e?.message || e);
            toast.error("Failed to load trials.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useRealtimeChannel("trials", load);

    const createTrial = async () => {
        setCreating(true);
        try {
            const { data } = await api.post("/trials", {
                title: "Untitled Trial",
                trial_date: todayIso(),
                outcome: "planned",
                seed_template: true,
            });
            toast.success("Trial created — pre-filled with standard checklists.");
            nav(`/trials/${data.id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Create failed.");
        } finally {
            setCreating(false);
        }
    };

    const upcoming = useMemo(
        () => trials.filter((t) => ["planned", "in-progress"].includes(t.outcome)).length,
        [trials]
    );
    const complete = useMemo(
        () => trials.filter((t) => ["success", "partial"].includes(t.outcome)).length,
        [trials]
    );
    const failed = useMemo(
        () => trials.filter((t) => ["failed", "aborted"].includes(t.outcome)).length,
        [trials]
    );

    return (
        <div className="min-h-screen md:h-screen bg-virtus-bg text-white flex flex-col md:overflow-hidden">
            <FocusHeader
                code="AO/06"
                title="Trials"
                subtitle="Field-test planning · execution capture · post-test debrief."
            />

            <div className="grid grid-cols-2 md:grid-cols-4 border-b border-virtus-line flex-shrink-0">
                <Metric label="TOTAL" value={trials.length} color="#FFFFFF" />
                <Metric label="UPCOMING" value={upcoming} color="#FFEA00" />
                <Metric label="COMPLETE" value={complete} color="#00E676" />
                <Metric label="LOSSES" value={failed} color="#FF1744" />
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between px-3 md:px-6 py-2 border-b border-virtus-line flex-shrink-0 gap-2 md:gap-0">
                <div className="font-mono text-[10px] tracking-[0.24em] md:tracking-[0.28em] uppercase text-virtus-dim truncate">
                    TRIALS // {trials.length}
                    <span className="hidden md:inline"> · SAY <span className="text-virtus-orange">"OPEN TRIAL N"</span></span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={() => setChecklistOpen(true)}
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim border border-virtus-line hover:border-virtus-orange hover:text-virtus-orange px-3 py-1.5"
                        data-testid="trials-pre-launch-checklist-btn"
                        title="Reference checklist for USV operators"
                    >
                        <ClipboardList size={11} />
                        <span className="hidden md:inline">PRE-LAUNCH CHECKLIST</span>
                        <span className="md:hidden">CHECKLIST</span>
                    </button>
                    <button
                        onClick={createTrial}
                        disabled={creating}
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-3 py-1.5 hover:bg-virtus-orange hover:text-black disabled:opacity-50"
                        data-testid="trials-new-btn"
                    >
                        {creating ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                        NEW TRIAL
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-auto p-3 md:p-6" data-kiosk-scroll="true">
                {loading ? (
                    <div className="flex items-center justify-center py-24 text-virtus-dim">
                        <Loader2 className="animate-spin" size={18} />
                    </div>
                ) : trials.length === 0 ? (
                    <EmptyState onCreate={createTrial} />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        <AnimatePresence>
                            {trials.map((t, i) => (
                                <TrialCard
                                    key={t.id}
                                    trial={t}
                                    index={i + 1}
                                    onClick={() => nav(`/trials/${t.id}`)}
                                />
                            ))}
                        </AnimatePresence>
                    </div>
                )}
            </div>
        </div>
    );
}

function Metric({ label, value, color }) {
    return (
        <div className="p-3 md:p-5 border-r border-virtus-line last:border-r-0">
            <div className="font-mono text-[9px] tracking-[0.24em] md:tracking-[0.28em] uppercase text-virtus-dim mb-1 md:mb-2">
                {label}
            </div>
            <div
                className="font-heading text-3xl md:text-4xl font-black tracking-tighter"
                style={{ color }}
            >
                {value}
            </div>
        </div>
    );
}

function TrialCard({ trial, index, onClick }) {
    const outcome = OUTCOME_META[trial.outcome] || OUTCOME_META.planned;
    return (
        <motion.button
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            onClick={onClick}
            className="text-left bg-virtus-surface border border-virtus-line hover:border-virtus-orange group transition-colors"
            data-testid={TID.trialCard?.(trial.id) || `trial-card-${trial.id}`}
        >
            <div className="flex items-center justify-between px-4 py-3 border-b border-virtus-line">
                <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] text-virtus-orange tracking-[0.24em]">
                        #{String(index).padStart(2, "0")}
                    </span>
                    <span
                        className="px-2 py-0.5 font-mono text-[9px] tracking-[0.2em] font-bold"
                        style={{ background: outcome.bg, color: outcome.fg }}
                    >
                        {outcome.label}
                    </span>
                </div>
                <ChevronRight size={14} className="text-virtus-dim group-hover:text-virtus-orange" />
            </div>
            <div className="px-4 py-4">
                <div className="font-heading text-lg font-bold text-white leading-tight tracking-tight mb-2">
                    {trial.title || "Untitled Trial"}
                </div>
                <div className="grid grid-cols-2 gap-y-1 font-mono text-[10px] uppercase tracking-[0.2em] text-virtus-dim">
                    <span className="flex items-center gap-1.5">
                        <Calendar size={10} />
                        {trial.trial_date || "TBD"}
                    </span>
                    <span className="flex items-center gap-1.5">
                        <Anchor size={10} />
                        {trial.vessel_callsign || "—"}
                    </span>
                    <span className="flex items-center gap-1.5 truncate col-span-2">
                        <MapPin size={10} />
                        {trial.primary_site || trial.location || "—"}
                    </span>
                    {trial.lead && (
                        <span className="flex items-center gap-1.5 truncate col-span-2">
                            <User size={10} />
                            {trial.lead}
                        </span>
                    )}
                </div>
            </div>
        </motion.button>
    );
}

function EmptyState({ onCreate }) {
    return (
        <div className="flex flex-col items-center justify-center py-24 text-center px-6">
            <Anchor size={40} className="text-virtus-orange mb-6" strokeWidth={1} />
            <div className="font-heading text-2xl font-bold mb-2">No trials logged yet.</div>
            <div className="text-virtus-dim text-sm mb-8 max-w-md">
                Create your first field test. A new trial starts with the standard VIRTUS MARITIME
                checklists — gear list, pre-op / pre-float / on-water checks, and testing goals.
            </div>
            <button
                onClick={onCreate}
                className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-5 py-2 hover:bg-virtus-orange hover:text-black"
                data-testid="trials-empty-create-btn"
            >
                <Plus size={11} /> CREATE FIRST TRIAL
            </button>
        </div>
    );
}
